import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { Button } from "../components/common/Button";
import { ShoppingCart, ArrowLeft, Package, Ruler, CheckCircle, Check, ChevronLeft, ChevronRight, Clock, Frame, Award, Tag, Sparkles, Bookmark } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { Spinner } from "../components/common/Spinner";
import { usePromotion, getDiscountedPrice } from "../hooks/usePromotion";
import styles from "./PaintKitDetailPage.module.css";

interface KitImage {
  url: string;
  isDefault: boolean;
}

interface KitCollection {
  _id: string;
  name: string;
}

interface PaintKitDetail {
  _id: string;
  name: string;
  description: string;
  price: number;
  size: string;
  imageUrl: string;
  images: KitImage[];
  difficulty: string;
  inStock: boolean;
  included: string[];
  paintingTime?: string;
  framed?: boolean;
  howToUse?: string;
  bestSeller?: boolean;
  latestCollection?: boolean;
  productCollection?: KitCollection | null;
  categories?: KitCollection[];
}

export function PaintKitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [kit, setKit] = useState<PaintKitDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { promotion, isActive } = usePromotion();
  const kitsDiscountPercent =
    isActive && promotion?.appliesTo.paintingKits
      ? promotion.discountPercent
      : 0;

  const resolvedImages: string[] = useMemo(() => {
    if (!kit) return [];
    if (kit.images && kit.images.length > 0) {
      const sorted = [...kit.images].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
      return sorted.map((img) =>
        img.url.startsWith("http") ? img.url : `${API_BASE_URL}${img.url}`
      );
    }
    if (kit.imageUrl) {
      const url = kit.imageUrl.startsWith("http")
        ? kit.imageUrl
        : `${API_BASE_URL}${kit.imageUrl}`;
      return [url];
    }
    return [];
  }, [kit]);

  useEffect(() => {
    const fetchKitDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/painting-kits/${id}`);
        if (!response.ok) {
          throw new Error("Failed to fetch kit details");
        }
        const data = await response.json();
        console.log("Fetched kit data:", data);
        setKit(data.kit);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching kit details:", error);
        setError("Failed to load kit details. Please try again.");
        setLoading(false);
      }
    };

    if (id) {
      fetchKitDetails();
    }
  }, [id]);

  const handleAddToCart = () => {
    if (!kit) return;
    addToCart({
      id: kit._id,
      name: kit.name,
      price:
        kitsDiscountPercent > 0
          ? getDiscountedPrice(kit.price, kitsDiscountPercent)
          : kit.price,
      imageUrl: resolvedImages[0] || kit.imageUrl,
      type: 'kit',
      size: kit.size,
    });
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Spinner label="Loading kit details..." size="lg" fullPage />
      </div>
    );
  }

  if (error || !kit) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>{error || "Kit not found"}</p>
          <Button onClick={() => navigate("/products/kits")} variant="primary">
            Back to Paint Kits
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.detailPage}>
      <div className={styles.container}>
        {/* Back Button */}
        <button onClick={() => navigate("/products/kits")} className={styles.backButton}>
          <ArrowLeft size={20} />
          Back to All Kits
        </button>

        {/* Main Content */}
        <div className={styles.content}>
          {/* Image Section */}
          <div className={styles.imageSection}>
            <img
              src={resolvedImages[selectedImageIndex] || kit.imageUrl}
              alt={kit.name}
              className={styles.mainImage}
            />
            {!kit.inStock && <div className={styles.outOfStock}>Out of Stock</div>}

            {resolvedImages.length > 1 && (
              <>
                <button
                  className={`${styles.navArrow} ${styles.navArrowLeft}`}
                  onClick={() => setSelectedImageIndex((prev) => prev === 0 ? resolvedImages.length - 1 : prev - 1)}
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  className={`${styles.navArrow} ${styles.navArrowRight}`}
                  onClick={() => setSelectedImageIndex((prev) => prev === resolvedImages.length - 1 ? 0 : prev + 1)}
                >
                  <ChevronRight size={24} />
                </button>

                <div className={styles.thumbnailStrip}>
                  {resolvedImages.map((imgUrl, idx) => (
                    <button
                      key={idx}
                      className={`${styles.thumbnail} ${idx === selectedImageIndex ? styles.thumbnailActive : ""}`}
                      onClick={() => setSelectedImageIndex(idx)}
                    >
                      <img src={imgUrl} alt={`${kit.name} ${idx + 1}`} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Details Section */}
          <div className={styles.detailsSection}>
            <div className={styles.header}>
              <h1 className={styles.title}>{kit.name}</h1>
            </div>

            <div className={styles.badgeRow}>
              {kit.bestSeller && (
                <span className={styles.bestSellerTag}>
                  <Award size={14} />
                  Best Seller
                </span>
              )}
              {kit.latestCollection && (
                <span className={styles.latestTag}>
                  <Sparkles size={14} />
                  New
                </span>
              )}
              {kit.productCollection && (
                <Link
                  to={`/products/kits?collection=${kit.productCollection._id}`}
                  className={styles.collectionTag}
                >
                  <Tag size={14} />
                  {kit.productCollection.name}
                </Link>
              )}
              {(kit.categories || []).map((cat) => (
                <Link
                  key={cat._id}
                  to={`/products/kits?category=${cat._id}`}
                  className={styles.categoryTag}
                >
                  <Bookmark size={14} />
                  {cat.name}
                </Link>
              ))}
              {kit.difficulty && (
                <span className={`${styles.difficultyTag} ${styles[kit.difficulty]}`}>
                  {kit.difficulty.charAt(0).toUpperCase() + kit.difficulty.slice(1)}
                </span>
              )}
            </div>

            <p className={styles.description}>{kit.description}</p>

            {kitsDiscountPercent > 0 ? (
              <div className={styles.priceGroup}>
                <span className={styles.originalPrice}>EGP {kit.price}</span>
                <span className={`${styles.price} ${styles.discountedPrice}`}>
                  EGP {getDiscountedPrice(kit.price, kitsDiscountPercent)}
                </span>
                <span className={styles.discountBadge}>
                  -{kitsDiscountPercent}%
                </span>
              </div>
            ) : (
              <div className={styles.price}>EGP {kit.price}</div>
            )}

            {/* Kit Info */}
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <Ruler className={styles.infoIcon} size={20} />
                <div>
                  <span className={styles.infoLabel}>Size</span>
                  <span className={styles.infoValue}>{kit.size}</span>
                </div>
              </div>
              <div className={styles.infoItem}>
                <Package className={styles.infoIcon} size={20} />
                <div>
                  <span className={styles.infoLabel}>Status</span>
                  <span className={styles.infoValue}>
                    {kit.inStock ? "In Stock" : "Out of Stock"}
                  </span>
                </div>
              </div>
              {kit.paintingTime && (
                <div className={styles.infoItem}>
                  <Clock className={styles.infoIcon} size={20} />
                  <div>
                    <span className={styles.infoLabel}>Painting Time</span>
                    <span className={styles.infoValue}>{kit.paintingTime}</span>
                  </div>
                </div>
              )}
              {kit.framed && (
                <div className={styles.infoItem}>
                  <Frame className={styles.infoIcon} size={20} />
                  <div>
                    <span className={styles.infoLabel}>Framed</span>
                    <span className={styles.infoValue}>Yes</span>
                  </div>
                </div>
              )}
            </div>

            {/* What's Included */}
            {kit.included && kit.included.length > 0 && (
              <div className={styles.includedSection}>
                <h2 className={styles.sectionTitle}>What's Included</h2>
                <ul className={styles.includedList}>
                  {kit.included.map((item, index) => (
                    <li key={index} className={styles.includedItem}>
                      <CheckCircle size={18} className={styles.checkIcon} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* How to Use */}
            {kit.howToUse && (
              <div className={styles.includedSection}>
                <h2 className={styles.sectionTitle}>How to Use</h2>
                <p className={styles.description}>{kit.howToUse}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className={styles.actions}>
              <Button
                variant="primary"
                onClick={handleAddToCart}
                disabled={!kit.inStock}
                className={styles.addToCartButton}
              >
                {addedToCart ? (
                  <>
                    <Check size={20} />
                    Added to Cart!
                  </>
                ) : (
                  <>
                    <ShoppingCart size={20} />
                    {kit.inStock ? "Add to Cart" : "Out of Stock"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
